<template>
  <div class="w-full min-h-screen flex flex-col">
      <MainNav :inverted="true" />
      <section class="w-full relative">
        <img 
                src="/image/83islands_yasurbw.jpg"
                alt="83 Islands Entropy Index" 
                class="w-full h-[800px] object-cover object-[bottom_center] md:object-center"
            />
        
        <!-- Text Overlay -->
        <div class="mx-auto absolute mt-36 inset-0 z-20 w-full p-4 lg:p-36 flex flex-col items-center lg:items-start pointer-events-none">
            <h1 class="text-white font-maax text-4xl uppercase lg:text-6xl font-bold text-center lg:text-left max-w-3xl leading-tight drop-shadow-[0_5px_5px_rgba(0,0,0,0.3)] pointer-events-auto">
              A world-first in rum. Born of Vanuatu. Led by nature.
            </h1>
        </div>

<!--  Double Wave -->
            <div class="absolute -bottom-1 left-0 right-0 w-full overflow-hidden">
                <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="w-full h-20 text-white">
                    <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" 
                          fill="currentColor" class="opacity-25"/>
                    <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" 
                          fill="currentColor"/>
                </svg>
            </div>

        </section>
      <main class="flex-grow w-full bg-white">
        <div class="container mx-auto p-4">
          <div class="flex flex-col gap-8">
            <div class="w-full">
              <h2 class="text-4xl font-maax font-bold uppercase text-black pb-4">83 ISLANDS ENTROPY INDEX™</h2>

              <!-- Answer-first capsule: definitional summary for readers and AI extractors -->
              <aside class="border-l-4 border-black bg-gray-50 px-6 py-5 mb-8" itemscope itemtype="https://schema.org/DefinedTerm">
                <p class="font-maax text-black text-base uppercase tracking-wider pb-2 font-bold">What is the 83 Islands Entropy Index™?</p>
                <p class="font-maax text-black text-lg leading-relaxed">
                  <strong itemprop="name">The 83 Islands Entropy Index™</strong> <span itemprop="description">is a proprietary framework, developed and trademarked by 83 Islands Distillery in Vanuatu, that quantifies how natural forces — atmospheric pressure swings, cyclone winds, and seismic activity along the Pacific Ring of Fire — shape rum during maturation. It is, to the distillery's knowledge, the first published methodology of its kind in the spirits industry. The framework comprises three sub-indices: the <strong>Pressure Flux Index™</strong> (PFI), the <strong>Wind Agitation Index™</strong> (WAI), and the <strong>Seismic Energy Index™</strong> (SEI), each derived from real meteorological and seismic data measured at the distillery's location in Port Vila.</span>
                </p>
              </aside>

              <p class="font-maax text-black text-xl pb-8">
                In Vanuatu, the land itself never sleeps. The wind shifts, the ground stirs, the air thickens and thins. Every movement transfers energy, every change shapes the spirit within our casks. At 83 Islands, we study these forces not to tame them, but to understand how they breathe life into rum. The Entropy Index™ is our living record of this dialogue between nature and the barrel—a scientific measure of the energy that moves through our spirit as it matures. Learn more about Vanuatu's unique <router-link to="/climate" class="underline hover:text-gray-600">tropical climate</router-link> and the <router-link to="/restless-earth" class="underline hover:text-gray-600">restless earth</router-link> that shapes our spirits.
              </p>

              <!-- The Three Indices -->
              <h3 class="text-3xl font-maax font-bold uppercase text-black pb-6">The Three Indices</h3>
              
              <p class="font-maax text-black text-xl pb-4">
                <strong>The Pressure Flux Index (PFI)</strong> captures the rhythm of the skies. When <router-link to="/restless-earth#cyclones" class="underline hover:text-gray-600">cyclones</router-link> draw near and the atmosphere plunges, each barrel exhales, drawing the rum deeper into the oak. These fluctuations in pressure accelerate the exchange of vanillin, tannin, and sweetness—nature's own pulse turning time into texture.
              </p>

              <div class="bg-gray-800 rounded-xl p-6 mb-6">
                <p class="font-maax text-white text-xl pb-4">
                  <strong>Pressure swings</strong> make the barrels breathe deeper — contracting and expanding, driving the rum into the wood's hidden veins.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  When the air above the islands shifts, so does the soul of the cask.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  At sea level, the sky presses down with a weight of 1,013 millibars. It is invisible, yet constant — the breath of the world resting upon every surface, every drop of rum. But when a cyclone approaches, that pressure collapses. A Category 5 storm can plunge to 935 millibars — the sky suddenly lighter by what feels like a mountain's worth of air.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  That 78-millibar fall is not subtle. It is a heaving of the atmosphere itself. A single millibar moves the wind. Seventy-eight carve spirals across the ocean and rip trees from the ground.
                </p>
                <p class="font-maax text-gray-300 text-xl">
                  In that moment, the casks begin to breathe deeper. The oak expands and contracts as the pressure around it swings. Air and spirit exchange places — the rum pushed into the pores, drawn back again, over and over, as if the barrel itself were inhaling the storm. Each drop marked by the pulse of the sky, each season a new rhythm between calm and chaos.
                </p>
              </div>

              <p class="font-maax text-black text-xl pb-4">
                <strong>The Wind Agitation Index (WAI)</strong> traces the mechanical heartbeat of the storm. Measured through real wind data, it quantifies the subtle vibrations that ripple through our warehouses as winds rise. Each micro-movement stirs the liquid against the wood, enhancing oxidation and contact in ways stillness never could.
              </p>

              <div class="bg-gray-800 rounded-xl p-6 mb-6">
                <p class="font-maax text-white text-xl pb-4">
                  <strong>Cyclone winds</strong> rock the warehouse and carry salt air through its gaps — oxygen and sea whispering into spirit.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  When the storm comes, the air itself becomes weight.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  A Category 5 cyclone does not simply blow — it strikes. Winds of 250 km/h or more race across the sea, faster than a highway car, carrying the ocean in their fists. This is not a single gust but a vast, spinning engine of air, the size of a continent, moving for days without rest.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  To grasp its power: At 250 km/h, the air itself becomes a weapon — peeling bark from trees, flattening forests, turning rain into shrapnel.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  At that velocity, wind stops behaving like air and starts behaving like water — a fluid hammer crashing into everything it meets. The pressure on a wall at 250 km/h is eight times greater than at 90 km/h. Each gust strikes like a wave, each pause a breath before the next blow.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  Inside the warehouse, our casks do not hide from this force. The wind seeps through gaps, carrying salt and oxygen from the reef. The spirit stirs, rocked in its cradle of oak, the wood sighing as the air outside roars.
                </p>
                <p class="font-maax text-gray-300 text-xl">
                  The cyclone's breath etches its memory into every barrel — the taste of motion, of pressure, of the storm itself.
                </p>
              </div>

              <p class="font-maax text-black text-xl pb-4">
                Beneath it all, <strong>the Seismic Energy Index (SEI)</strong> records the quiet tremors of the earth itself. Derived from real seismic readings, it reflects how deep forces—magnitude, distance, and geology—whisper through the racks, imparting gentle waves of motion that no human hand could reproduce.
              </p>

              <div class="bg-gray-800 rounded-xl p-6 mb-6">
                <p class="font-maax text-white text-xl pb-4">
                  <strong>Earthquakes</strong> stir the rum within the cask — a quiet rhythm beneath the oak, a heartbeat of the island itself.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  Vanuatu lies on the <router-link to="/restless-earth" class="text-gray-300 underline hover:text-white">Ring of Fire</router-link> — a chain of restless mountains rising from the sea. Here, the earth hums with hidden energy: plates grinding, faults whispering, magma shifting in the dark. Every tremor, from the faintest quiver to the deep roll of a quake, sends its pulse through soil, foundation, and cask.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  But earthquakes are not measured in simple numbers. The Richter scale — or its modern form, the Moment Magnitude — is logarithmic, not linear.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  A magnitude 4 quake is not a small step up from a 3; it is 31 times stronger.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  A magnitude 6 is nearly 1,000 times greater.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  By the time you reach magnitude 7, the energy released equals the detonation of several nuclear bombs — all from the quiet shifting of stone beneath the sea.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  To measure this, our Seismic Energy Index draws on the same physics used by seismologists:
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4 italic">
                  Energy = 10^(1.5 × Magnitude) joules.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  We sum each tremor's energy across the year, scaled to the casks' location, to reveal how much motion the earth has poured into the rum — a ledger of invisible power.
                </p>
                <p class="font-maax text-gray-300 text-xl pb-4">
                  When the ground moves, the barrels move. The spirit inside stirs — slow, rhythmic, patient — a heartbeat transmitted from the planet itself.
                </p>
                <p class="font-maax text-gray-300 text-xl">
                  <router-link to="/restless-earth#earthquakes" class="text-gray-300 underline hover:text-white">View earthquake and cyclone data since we opened →</router-link>
                </p>
              </div>

              <p class="font-maax text-black text-xl pb-8">
                Together, these metrics form the Entropy Index™: a portrait of energy in motion, of transformation made visible. In most places, such forces would be seen as disruption. Here, they are the breath of creation. Each number tells a story of movement, each cask a testament to the land's vitality.
              </p>

              <!-- Legal & Marks -->
              <h4 class="text-xl font-maax font-bold text-black pb-4">Legal & Marks</h4>
              
              <p class="font-maax text-gray-600 text-sm pb-8">
                ENTROPY INDEX™, PRESSURE FLUX INDEX™, WIND AGITATION INDEX™, and SEISMIC ENERGY INDEX™ are trademarks of 83 Islands. All scientific terms are simplified for clarity. Full data and calculations are available on request.
              </p>
            </div>
          </div>
        </div>

        <!-- SVG Pattern Overlay -->
   <!--      <div 
          class="bottom-8 left-0 right-0 z-10 w-full h-24 bg-repeat-x"
          style="background-image: url('/image/83-Teouma-web.svg');">
        </div> -->
      </main>
      <Footer class="relative" />
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, onUnmounted } from 'vue';
  import MainNav from '../components/MainNav.vue'
  import Footer from '../components/Footer.vue';

  const showHeader = ref(true);
    let lastScrollPosition = 0;

    const handleScroll = () => {
        const currentScrollPosition = window.scrollY;
        
        // Show header when scrolling up or at top
        showHeader.value = 
            currentScrollPosition < lastScrollPosition || 
            currentScrollPosition < 50;
        
        lastScrollPosition = currentScrollPosition;
    };

    // Add and remove scroll listener
    onMounted(() => {
        window.addEventListener('scroll', handleScroll);
    });

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll);
    });
</script>


<style>
/* Optional: Add smooth transition for header */
header {
    transition: transform 0.3s ease;
}
</style>

